import {
  AddGroupMemberResponse,
  MEMBERS_VISIBLE_WARNING_REV,
  StudentGroupCard,
  StudentGroupListResponse,
} from "@edu/shared";
import type { AddGroupMemberRequest, UpdateGroupRequest } from "@edu/shared";
import { call } from "../../lib/api.ts";

/**
 * Восемь ручек групп (`apps/api/src/routes/groups.ts`).
 *
 * Живут здесь, а не в `lib/api.ts`, по соседской причине: ручки нужны ровно
 * двум экранам, и держать их рядом с экранами дешевле, чем в общем клиенте на
 * девятьсот строк. Базовый адрес, cookie и разбор отказов при этом общие:
 * наружу отсюда уезжает только `call`, второго HTTP-клиента не заводится.
 *
 * ⚠️ **Довода про стартовый чанк здесь больше нет, и его не надо возвращать.**
 * До ревью 29.08.2026 в этой шапке было написано, что иначе
 * `@edu/shared/groups.ts` уедет в стартовый чанк ученику. Это неправда: он там
 * и так, потому что `packages/shared/src/announcements.ts` импортирует оттуда
 * `GroupId` и `MAILING_LIST_MEMBERS_MAX` ЗНАЧЕНИЕМ, а рассылка лежит в
 * стартовом чанке обеих сторон. Починить это можно только в контрактах,
 * перенеся эти два имени в нейтральный модуль; пока не перенесено, файл стоит
 * здесь по соседству, а не ради веса бандла.
 *
 * ⚠️ **Все восемь ручек преподавательские, ученических среди них нет ни одной.**
 * Единственный предикат каждого запроса на сервере это `teacher_user_id = :я`,
 * поэтому ученик своей же группы получает то же, что посторонний: пустой список
 * и 404 на карточку. Экраны, которые ходят сюда, обязаны грузиться ленивым
 * чанком и стоять за `isTeacherSide`.
 *
 * ⚠️ **Пишущие ручки отвечают КАРТОЧКОЙ, а не 204** (включая удаление
 * участника): состав и `memberCount` меняются связкой, и второй круг по сети за
 * тем, что сервер только что посчитал, на канале 128 кбит/с человек видит как
 * подвисание. Поэтому здесь у каждой мутации тип возврата `StudentGroupCard`, и
 * экран кладёт ответ прямо в состояние, а не перезапрашивает карточку.
 */

export function listGroups(): Promise<StudentGroupListResponse> {
  return call("/v1/groups", {}, StudentGroupListResponse);
}

export function getGroup(groupId: string): Promise<StudentGroupCard> {
  return call(`/v1/groups/${groupId}`, {}, StudentGroupCard);
}

export function createGroup(title: string): Promise<StudentGroupCard> {
  return call("/v1/groups", { method: "POST", body: { title } }, StudentGroupCard);
}

/**
 * Правка группы: название, видимость состава, архив.
 *
 * ⚠️ При включении видимости состава тело обязано нести `warningRev`, и берётся
 * оно из `MEMBERS_VISIBLE_WARNING_REV` в `@edu/shared`, а не пишется литералом:
 * сервер сверяет присланное со своей константой и отвечает `group_warning_stale`
 * на расхождение. Собирает такое тело `GroupVisibility.tsx`, здесь ручка
 * остаётся общей.
 */
export function updateGroup(groupId: string, patch: UpdateGroupRequest): Promise<StudentGroupCard> {
  return call(`/v1/groups/${groupId}`, { method: "PATCH", body: patch }, StudentGroupCard);
}

/**
 * Предупреждение показали заново, и человек его закрыл.
 *
 * ⚠️ **Ручка отдельная, потому что в группе не меняется ничего.** Состав у
 * такой группы открыт давно, и переписывать `warnedTextRev` под новую редакцию
 * нельзя: он правдиво говорит, под какими словами это случилось. Здесь
 * дописывается строка журнала, и на неё же смотрит `warnedCurrentRev` в
 * карточке.
 *
 * Редакция берётся из константы, а не пишется литералом: сервер сверяет
 * присланное со своей и отвечает `group_warning_stale` на расхождение, то есть
 * вкладка, открытая до правки текста, записи не оставит.
 */
export function recordGroupWarning(groupId: string): Promise<StudentGroupCard> {
  return call(
    `/v1/groups/${groupId}/warnings`,
    { method: "POST", body: { warningRev: MEMBERS_VISIBLE_WARNING_REV } },
    StudentGroupCard,
  );
}

/**
 * ⚠️ Ответ ШИРЕ карточки: кроме неё приезжает судьба расписания
 * (`scheduleSkipped`). Поле необязательное, и его отсутствие означает «серия
 * заведена либо не была нужна», а присутствие означает, что человек в группе, а
 * в расписании его нет. Разбор у `AddGroupMemberResponse` в контрактах.
 */
export function addGroupMember(
  groupId: string,
  body: AddGroupMemberRequest,
): Promise<AddGroupMemberResponse> {
  return call(`/v1/groups/${groupId}/members`, { method: "POST", body }, AddGroupMemberResponse);
}

export function setGroupMemberName(
  groupId: string,
  studentUserId: string,
  displayName: string,
): Promise<StudentGroupCard> {
  return call(
    `/v1/groups/${groupId}/members/${studentUserId}`,
    { method: "PATCH", body: { displayName } },
    StudentGroupCard,
  );
}

export function removeGroupMember(
  groupId: string,
  studentUserId: string,
): Promise<StudentGroupCard> {
  return call(
    `/v1/groups/${groupId}/members/${studentUserId}`,
    { method: "DELETE" },
    StudentGroupCard,
  );
}
